import React from 'react';

/**
 * Vista imprimible de Nota Crédito (cuando no hay PDF DIAN).
 * Misma estructura visual que VistaPreviaFactura.
 */
function VistaPreviaNotaCredito({
    notaCredito,
    factura,
    client,
    paciente,
    encabezadoPersonalizado,
    facturador,
    contenedorId = 'nota-credito-preview',
}) {
    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        const soloDia = String(fecha).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (soloDia) {
            return `${soloDia[3]}/${soloDia[2]}/${soloDia[1].slice(-2)}`;
        }
        const date = new Date(fecha);
        if (Number.isNaN(date.getTime())) return String(fecha);
        const dia = String(date.getDate()).padStart(2, '0');
        const mes = String(date.getMonth() + 1).padStart(2, '0');
        const anio = String(date.getFullYear()).slice(-2);
        return `${dia}/${mes}/${anio}`;
    };

    const formatearMoneda = (valor) =>
        Math.round(parseFloat(valor || 0)).toLocaleString('es-CO');

    const obtenerNit = () => {
        if (!paciente) return '';
        return paciente.numero_documento || paciente.numeroDocumento || '';
    };

    const construirDireccionCompleta = () => {
        if (!paciente) return '';
        const partes = [];
        if (paciente.direccion) partes.push(paciente.direccion);
        if (paciente.zona) partes.push(paciente.zona);
        if (paciente.nombre_ciudad_residencia) partes.push(paciente.nombre_ciudad_residencia);
        if (paciente.nombre_departamento_residencia) partes.push(paciente.nombre_departamento_residencia);
        if (paciente.nombre_pais_residencia) partes.push(paciente.nombre_pais_residencia);
        return partes.join(', ') || paciente.direccion || '';
    };

    const nombrePaciente = paciente
        ? `${paciente.primer_nombre || ''} ${paciente.segundo_nombre || ''} ${paciente.primer_apellido || ''} ${paciente.segundo_apellido || ''}`.trim()
        : 'N/A';

    const numeroNc =
        notaCredito?.numero_completo ||
        `${notaCredito?.prefijo || ''}${notaCredito?.numero_nc || ''}` ||
        '—';

    const numeroFactura =
        `${factura?.prefijo || ''}${factura?.prefijo ? ' ' : ''}${factura?.numero_factura || ''}`.trim() ||
        factura?.numero_factura ||
        '—';

    const esElectronica = notaCredito?.tipo === 'electronica' || !!notaCredito?.uuid;
    const tituloBanner = esElectronica
        ? `NOTA CRÉDITO ELECTRÓNICA: ${numeroNc}`
        : `NOTA CRÉDITO: ${numeroNc}`;

    const generarEncabezadoHTML = () => {
        if (!encabezadoPersonalizado) return null;
        return (
            <div style={{ marginBottom: '10px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#ffffff' }}>
                    <tbody>
                        <tr>
                            <td
                                style={{ width: '30%', padding: '2px', fontSize: '10px', lineHeight: '1.1', verticalAlign: 'top', wordWrap: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: encabezadoPersonalizado.izquierda }}
                            />
                            <td
                                style={{ width: '40%', padding: '2px', textAlign: 'center', fontSize: '11px', lineHeight: '1.1', verticalAlign: 'top', wordWrap: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: encabezadoPersonalizado.centro }}
                            />
                            <td
                                style={{ width: '30%', padding: '2px', textAlign: 'right', fontSize: '10px', lineHeight: '1.1', verticalAlign: 'top', wordWrap: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: encabezadoPersonalizado.derecha }}
                            />
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    if (!notaCredito) {
        return null;
    }

    return (
        <div className="container-fluid p-0" id={contenedorId}>
            <div className="invoice p-2" id="nota-credito-invoice-content">
                {generarEncabezadoHTML()}

                <div className="row m-0 mb-2">
                    <div className="col-12 p-0">
                        <div
                            style={{
                                backgroundColor: '#e9ecef',
                                padding: '4px 8px',
                                fontWeight: 'bold',
                                fontSize: '10px',
                                borderRadius: '3px',
                                textAlign: 'center',
                                border: '1px solid #dee2e6',
                            }}
                        >
                            {tituloBanner}
                        </div>
                    </div>
                </div>

                <div className="row m-0 mb-2">
                    <div className="col-12 p-0">
                        <table className="table table-bordered w-100 mb-0" style={{ fontSize: '10px' }}>
                            <tbody>
                                <tr>
                                    <td className="bg-light font-weight-bold align-middle p-1" style={{ width: '15%' }}>
                                        NOTA #
                                    </td>
                                    <td className="bg-white p-1" style={{ width: '18%' }}>
                                        {numeroNc}
                                    </td>
                                    <td className="bg-light font-weight-bold align-middle p-1" style={{ width: '15%' }}>
                                        PREFIJO
                                    </td>
                                    <td className="bg-white p-1" style={{ width: '18%' }}>
                                        {notaCredito.prefijo || 'N/A'}
                                    </td>
                                    <td className="bg-light font-weight-bold align-middle p-1" style={{ width: '15%' }}>
                                        EMISIÓN
                                    </td>
                                    <td className="bg-white p-1" style={{ width: '19%' }}>
                                        {formatearFecha(notaCredito.fecha_emision)}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="bg-light font-weight-bold align-middle p-1">FACTURA REF.</td>
                                    <td className="bg-white p-1">{numeroFactura}</td>
                                    <td className="bg-light font-weight-bold align-middle p-1">TIPO</td>
                                    <td className="bg-white p-1">
                                        {esElectronica ? 'Electrónica' : 'No electrónica'}
                                    </td>
                                    <td className="bg-light font-weight-bold align-middle p-1">ELABORÓ</td>
                                    <td className="bg-white p-1">
                                        {facturador
                                            ? `${facturador.name || ''} ${facturador.lastname || ''}`.trim()
                                            : 'N/A'}
                                    </td>
                                </tr>
                                {client?.nombre || client?.nombre_comercial ? (
                                    <tr>
                                        <td className="bg-light font-weight-bold align-middle p-1">EMPRESA</td>
                                        <td colSpan="5" className="bg-white p-1">
                                            {client.nombre_comercial || client.nombre}
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="row m-0 mb-2">
                    <div className="col-12 p-0">
                        <table className="table table-bordered w-100 mb-0" style={{ fontSize: '10px' }}>
                            <tbody>
                                <tr>
                                    <td className="bg-light font-weight-bold align-middle p-1" style={{ width: '15%' }}>
                                        RAZÓN SOCIAL
                                    </td>
                                    <td className="bg-white p-1" style={{ width: '35%' }}>
                                        {nombrePaciente}
                                    </td>
                                    <td className="bg-light font-weight-bold align-middle p-1" style={{ width: '15%' }}>
                                        NIT / DOC.
                                    </td>
                                    <td className="bg-white p-1" style={{ width: '35%' }}>
                                        {obtenerNit()}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="bg-light font-weight-bold align-middle p-1">TELÉFONO</td>
                                    <td className="bg-white p-1">{paciente?.telefono || paciente?.celular || ''}</td>
                                    <td className="bg-light font-weight-bold align-middle p-1">CELULAR</td>
                                    <td className="bg-white p-1">{paciente?.celular || ''}</td>
                                </tr>
                                <tr>
                                    <td className="bg-light font-weight-bold align-middle p-1">EMAIL</td>
                                    <td className="bg-white p-1">{paciente?.email || ''}</td>
                                    <td className="bg-light font-weight-bold align-middle p-1">DIRECCIÓN</td>
                                    <td className="bg-white p-1">{construirDireccionCompleta()}</td>
                                </tr>
                                {notaCredito.uuid ? (
                                    <tr>
                                        <td className="bg-light font-weight-bold align-middle p-1">CUDE</td>
                                        <td colSpan="3" className="bg-white p-1 text-break" style={{ fontSize: '9px' }}>
                                            {notaCredito.uuid}
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="row m-0 mb-2">
                    <div className="col-12 p-0">
                        <table className="table table-bordered w-100 mb-0" style={{ fontSize: '10px' }}>
                            <thead>
                                <tr className="bg-light">
                                    <th className="p-1 text-left">Concepto</th>
                                    <th className="p-1 text-right" style={{ width: '25%' }}>
                                        Valor
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="bg-white p-1">
                                        Nota crédito aplicada a factura {numeroFactura}
                                        {notaCredito.motivo_observacion
                                            ? `. Motivo: ${notaCredito.motivo_observacion}`
                                            : ''}
                                    </td>
                                    <td className="bg-white p-1 text-right font-weight-bold">
                                        ${formatearMoneda(notaCredito.valor_total)}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="text-right font-weight-bold p-1">Total nota crédito:</td>
                                    <td className="text-right font-weight-bold p-1">
                                        ${formatearMoneda(notaCredito.valor_total)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {notaCredito.motivo_observacion ? (
                    <div className="row m-0 mt-2">
                        <div className="col-12 p-0">
                            <h6 style={{ fontSize: '10px', marginBottom: '2px', fontWeight: 'bold' }}>Motivo:</h6>
                            <p style={{ fontSize: '10px', marginBottom: 0 }}>{notaCredito.motivo_observacion}</p>
                        </div>
                    </div>
                ) : null}

                {notaCredito.uuid ? (
                    <div className="row m-0 mt-3" style={{ borderTop: '1px solid #dee2e6', paddingTop: '8px' }}>
                        <div className="col-12 p-0">
                            <p
                                style={{
                                    fontSize: '8px',
                                    color: '#666',
                                    marginBottom: 0,
                                    textAlign: 'justify',
                                    lineHeight: '1.3',
                                }}
                            >
                                <strong>Nota Legal:</strong> Esta nota crédito electrónica ha sido generada conforme a la
                                normativa DIAN vigente. El CUDE garantiza la autenticidad e integridad del documento.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="row m-0 mt-3" style={{ borderTop: '1px solid #dee2e6', paddingTop: '8px' }}>
                        <div className="col-12 p-0">
                            <p style={{ fontSize: '8px', color: '#666', marginBottom: 0 }}>
                                Documento interno de nota crédito (sin PDF DIAN). Uso administrativo / archivo.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body, html, #root {
                        height: auto;
                        margin: 0;
                        padding: 0;
                        background-color: white;
                    }
                    .container-fluid {
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    .invoice {
                        width: 100% !important;
                        padding: 0 !important;
                        margin: 0 !important;
                    }
                    .row {
                        page-break-inside: avoid;
                        margin: 0 !important;
                    }
                    table {
                        page-break-inside: auto;
                        width: 100% !important;
                    }
                    tr {
                        page-break-inside: avoid;
                        page-break-after: auto;
                    }
                    td, th {
                        padding: 2px !important;
                    }
                    @page {
                        size: landscape;
                        margin: 5mm;
                    }
                }
            `}</style>
        </div>
    );
}

export default VistaPreviaNotaCredito;
